import { useMode } from '@apps/web/lib/mode_store';
import { useAnsweringNumbers } from '@apps/web/swr_data_fetching/data_fetching_hooks';

/**
 * Whether a voice step is likely to place a call for this organization.
 *
 * Checks only the coarsest of the worker's three dispatch gates — a
 * voice-enabled Phoebe number in the current mode. `OrganizationVoiceSettings`
 * and the transfer destination are not visible to the browser, so this can
 * still over-promise a call for a half-provisioned agency.
 *
 * Answers true whenever the numbers are unknown: still loading, or hidden from
 * a viewer without settings access, where the endpoint returns an empty list
 * rather than a 403. Guessing false there would strip the call from the
 * dominant playbook for every coordinator who cannot manage phone numbers.
 */
export function useCanPlaceCalls(organizationId: string | null): boolean {
    const mode = useMode();
    const { data: answeringNumbers } = useAnsweringNumbers(organizationId);
    if (!answeringNumbers?.can_manage_number_config) {
        return true;
    }
    return answeringNumbers.phoebe_phone_numbers.some(
        (phoneNumber) =>
            phoneNumber.voice_enabled && phoneNumber.mode === (mode ?? 'live'),
    );
}
